import { GlobalTelemetryConstants } from "./telemetry-constants";
import { ServiceDeskConfig } from "../../ServiceDesk/Startup/serviceDeskConfig";
import { ContextService, PresenceDataKey } from "../../ServiceDesk/sdServices/context/context-service";

export class TelemetryTemplateService {
  private defaultErrorEvent = this.getDefaultError();
  private defaultOutgoingRequestEvent = this.getDefaultOutgoingRequest();
  private defaultPageActionEvent = this.getDefaultPageActionEvent();
  private defaultPageViewEvent = this.getDefaultPageViewEvent();
  public defaultPartBForEvent: { [key: string]: {} } = {};
  public defaultPartBTypeForEvent: { [key: string]: string } = {};

  // @ngInject
  constructor(
    private sdConfig: ServiceDeskConfig,
    private contextService: ContextService,
    private telemetryConstants: GlobalTelemetryConstants
  ) {
    // TODO: There's an obvious strangeness to having three different programmatic constructs around the
    //  same set of keys. Maybe just having one hash map that contains the three different pieces of data
    //  instead?
    this.defaultPartBForEvent[this.telemetryConstants.eventsByName.AgentFeedback] = this.defaultPageActionEvent;
    this.defaultPartBForEvent[this.telemetryConstants.eventsByName.Error] = this.defaultErrorEvent;
    this.defaultPartBForEvent[this.telemetryConstants.eventsByName.PageView] = this.defaultPageViewEvent;
    this.defaultPartBForEvent[this.telemetryConstants.eventsByName.OutgoingRequest] = this.defaultOutgoingRequestEvent;

    const msTelemetryErrorName = "Ms.Telemetry.Error";
    const msQosOutgoingServiceRequestName = "Ms.Qos.OutgoingServiceRequest";
    const msContentPageActionEventName = "Ms.Content.PageAction";
    const msContentPageViewEventName = "Ms.Content.PageView";

    this.defaultPartBTypeForEvent[this.telemetryConstants.eventsByName.AgentFeedback] = msContentPageActionEventName;
    this.defaultPartBTypeForEvent[this.telemetryConstants.eventsByName.Error] = msTelemetryErrorName;
    this.defaultPartBTypeForEvent[this.telemetryConstants.eventsByName.PageView] = msContentPageViewEventName;
    this.defaultPartBTypeForEvent[this.telemetryConstants.eventsByName.OutgoingRequest] =
      msQosOutgoingServiceRequestName;
  }

  public getDefaultPartCForEvent(eventName: string) {
    let context = this.contextService.current;
    let appContext = this.contextService.appContext;
    let presence = this.contextService.getExtendedContextData(PresenceDataKey);
    let defaultPartCEvent: any = {
      agentId: appContext.agentId || "no-agent-id",
      agentEmail: appContext.agentUpn || appContext.agentEmail,
      vendorSiteId: appContext.vendorSiteId || "no-vendor-site-id",
      vendorSiteName: appContext.vendorSiteName || "no-vendor-site-name",
      sdVersion: this.sdConfig.version,
      viewId: context.stateName,
      currentUrl: this.getUri(context.url),
      // Legacy Features
      presence: presence ? presence : "no-presence",
      agentMode: appContext.agentMode || "no-agent-mode"
    };
    if (context.externalContextDetail) {
      if (context.externalContextDetail.externalAgentDetail1) {
        defaultPartCEvent.externalAgentDetail1 = context.externalContextDetail.externalAgentDetail1;
      }
      if (context.externalContextDetail.externalAgentDetail2) {
        defaultPartCEvent.externalAgentDetail2 = context.externalContextDetail.externalAgentDetail2;
      }
      if (context.externalContextDetail.externalSessionDetail1) {
        defaultPartCEvent.externalSessionDetail1 = context.externalContextDetail.externalSessionDetail1;
      }
      if (context.externalContextDetail.externalCustomerDetail1) {
        defaultPartCEvent.externalCustomerDetail1 = context.externalContextDetail.externalCustomerDetail1;
      }
    }
    switch (eventName) {
      case this.telemetryConstants.eventsByName.AgentFeedback:
        return angular.extend(defaultPartCEvent, {
          sdVersion: "no-version", // TODO: sessionContextService
          feedbackType: 3, // "General" <--See SD Bond File
          feedbackData: "no-feedback-data"
        });
      case this.telemetryConstants.eventsByName.Error:
        return angular.extend(defaultPartCEvent, {
          code: "no-code",
          detailedErrorData: "no-error-details",
          isUnhandled: false
        });
      case this.telemetryConstants.eventsByName.Information:
        return angular.extend(defaultPartCEvent, {
          informationType: 0, // "Information" <--See SD Bond File
          informationData: "no-data"
        });
      case this.telemetryConstants.eventsByName.OutgoingRequest:
        return angular.extend(defaultPartCEvent, {
          requestId: "",
          browserUserAgent: navigator.userAgent,
          requestContent: "no-request-content",
          responseContent: "no-response-content"
        });
      case this.telemetryConstants.eventsByName.PageView:
        return angular.extend(defaultPartCEvent, {
          pageViewData: "no-page-view-data"
        });
      default:
        return defaultPartCEvent;
    }
  }

  public getDefaultPartBTypeForEvent(eventName: string) {
    return this.defaultPartBTypeForEvent[eventName];
  }

  public getDefaultPartBForEvent(eventName: string) {
    return this.defaultPartBForEvent[eventName];
  }

  private getPageReferrer() {
    // Note: This isn't easily unit testable by angular standards. See http://stackoverflow.com/questions/32762216/why-document-referrer-in-angular-js-returning-blank-but-when-i-use-document-re for an alternative.
    return document.referrer;
  }

  private getDefaultError() {
    return {
      errorName: "no-error-name",
      errorMessage: "no-error-message",
      severity: 3,
      errorType: "General",
      errorCode: 0,
      lineNumber: 0,
      isDisplayed: false,
      errorLocation: "no-error-location",
      errorMethod: "no-error-method"
    };
  }

  private getDefaultOutgoingRequest() {
    return {
      operationName: "sdOutgoingRequest",
      targetUri: "",
      latencyMs: 0,
      serviceErrorCode: 0,
      succeeded: false,
      requestMethod: "GET",
      responseContentType: "",
      protocol: "HTTP",
      protocolStatusCode: "",
      dependencyOperationName: "ServiceDesk",
      dependencyOperationVersion: this.sdConfig.version,
      dependencyName: "SD",
      dependencyType: "WebService",
      responseSizeBytes: 0
    };
  }

  private getDefaultPageActionEvent() {
    return {
      pageName: "index",
      uri: this.getPageReferrer() || "",
      destUri: "",
      pageType: "",
      pageTags: "",
      product: "SD",
      screenState: 0, // Undefined
      customSessionGuid: "",
      impressionGuid: "",
      actionInputMethod: 4, // Mouse
      behavior: 0, // Undefined
      contentJsonVer: 0,
      content: ""
    };
  }

  private getDefaultPageViewEvent() {
    return {
      pageName: "index",
      uri: "",
      referrerUri: this.getPageReferrer(),
      pageType: "",
      pageTags: "",
      product: "SD",
      screenState: 0, // Undefined
      customSessionGuid: "",
      impressionGuid: "",
      contentJsonVer: 0,
      content: ""
    };
  }

  private getUri(referrerUri: string) {
    if (referrerUri && typeof referrerUri === "string") {
      // remove bearer token if present in uri.
      if (referrerUri.indexOf("id_token") >= 0) {
        referrerUri = referrerUri.split("id_token")[0];
      } else if (referrerUri.indexOf("access_token") >= 0) {
        referrerUri = referrerUri.split("access_token")[0];
      }
    }
    return referrerUri;
  }
}
